import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Tooltip from 'components/@extended/Tooltip'
import ComponentDisplay from 'components/ComponentDisplay'
import ComponentField from 'components/ComponentField'
import CurrencyField from 'components/CurrencyField'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import useAuth from 'hooks/useAuth'
import { useCreatePlan, usePlanRevision } from 'hooks/usePlans'
import {
  ExpandedCompensationTemplate,
  PrepareRolloverResponse,
  TargetPair,
  useTemplateRollover,
} from 'hooks/useRollovers'
import { useSnackbar } from 'notistack'
import React, { createContext, FC, useEffect, useMemo, useState } from 'react'
import type { CompensationTemplate } from 'types/comp-template'
import {
  ComponentSourceType,
  ComponentType,
  CompositeTargetValue,
  type CompositeTargetValueItem,
} from 'types/components'
import type { Period } from 'types/periods'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { findPeriodById, formatDisplayDate } from 'types/periods'

dayjs.extend(utc)

// Helper function to get the depth level of a period in the tree
const getPeriodDepth = (periods: Period[], periodId: string): number => {
  const findDepth = (periodList: Period[], targetId: string, currentDepth: number): number => {
    for (const period of periodList) {
      if (period.id === targetId) {
        return currentDepth
      }
      if (period.children && period.children.length > 0) {
        const childDepth = findDepth(period.children, targetId, currentDepth + 1)
        if (childDepth !== -1) {
          return childDepth
        }
      }
    }
    return -1
  }

  return findDepth(periods, periodId, 0)
}

interface PlanToRollover {
  id: string
  revision: number
  userId: string
  templateId: string
  employeeFirstName: string
  employeeLastName: string
  baseSalary: number
  position: string
  periodProration: number
  compensationModel: string
  variablePercent: number
  variableCap: number
}

interface RolloverPlanDialogProps {
  open: boolean
  onClose: () => void
  onRollover: (periodId: string) => void
  periods: Period[]
  isLoading?: boolean
  theme?: Theme
  fromPeriodLabel?: string
  fromPeriodId?: string
  plans: PlanToRollover[]
  periodIdLabelMap: Record<string, string>
  showSelectPeriodStep?: boolean
}

interface RolloverContext {
  promise: Promise<void> | null
  setPromise: (promise: Promise<void> | null) => void
}

const RolloverContext = createContext<RolloverContext>({
  promise: null,
  setPromise: () => {},
})

type Step = 'select-period' | 'target-review' | 'plan-edit' | 'plan-review'

const RolloverPlanDialog: FC<RolloverPlanDialogProps> = ({
  open,
  onClose,
  onRollover,
  periods,
  isLoading = false,
  theme,
  fromPeriodLabel,
  fromPeriodId,
  plans,
  periodIdLabelMap,
  showSelectPeriodStep = true,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { token } = useAuth()
  const muiTheme = useTheme()

  const { prepareTemplateRollover, executeTemplateRollover } = useTemplateRollover(token!)
  const { createPlan, isLoading: isCreatingPlan } = useCreatePlan(token!)

  // For multiple plans, we'll use the first plan's data for template operations since they share the same template
  const firstPlan = plans[0]
  const { compPlan, isLoading: isPlanLoading } = usePlanRevision(
    token!,
    firstPlan?.userId || '',
    firstPlan?.id || '',
    firstPlan?.revision || 1,
    open && !!firstPlan
  )

  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')
  const [activeStep, setActiveStep] = useState<Step>('select-period')
  const [isPreparingRollover, setIsPreparingRollover] = useState(false)
  const [isExecutingRollover, setIsExecutingRollover] = useState(false)
  const [templateTargetPairs, setTemplateTargetPairs] = useState<TargetPair[]>([])
  const [individualTargetPairs, setIndividualTargetPairs] = useState<TargetPair[]>([])
  const [prepareResponse, setPrepareResponse] = useState<PrepareRolloverResponse | null>(null)
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState<string>('Selected Period')
  const [validationError, setValidationError] = useState<string>('')
  const [template, setTemplate] = useState<ExpandedCompensationTemplate | CompensationTemplate | null>(null)
  const [rolloverPromise, setRolloverPromise] = useState<Promise<void> | null>(null)

  // Plan review/edit state - now handling multiple plans
  const [editablePlansData, setEditablePlansData] = useState<
    Record<string, { baseSalary: string; effectiveDate: string }>
  >({})

  // Composite target values state - key is compositeKey (componentId-periodId-userId)
  const [compositeTargetValues, setCompositeTargetValues] = useState<Record<string, CompositeTargetValueItem[]>>({})

  // Expanded composite rows state - key is compositeKey
  const [expandedCompositeRows, setExpandedCompositeRows] = useState<Record<string, boolean>>({})

  // Helper to get plan key
  const getPlanKey = (planId: string, revision: number) => `${planId}-${revision}`

  // Get the depth of the plan's period for the SinglePeriodSelector
  const planPeriodDepth = useMemo(() => {
    if (!fromPeriodId || !periods.length) {
      return undefined
    }

    const depth = getPeriodDepth(periods, fromPeriodId)
    return depth === -1 ? undefined : depth
  }, [periods, fromPeriodId])

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedPeriodId('')
      setActiveStep('select-period')
      setSelectedPeriodLabel('Selected Period')
      setValidationError('')
      setTemplateTargetPairs([])
      setIndividualTargetPairs([])
      setPrepareResponse(null)
      setTemplate(null)
      setRolloverPromise(null)
      setEditablePlansData({})
      setCompositeTargetValues({})
      setExpandedCompositeRows({})
    }
  }, [open])

  // Initialize editable fields for all plans when plan data is available
  useEffect(() => {
    if (compPlan?.plan && selectedPeriodId && plans.length > 0) {
      const selectedPeriod = findPeriodById(periods, selectedPeriodId)
      const effectiveDate = selectedPeriod?.startDate || ''

      const newEditableData: Record<string, { baseSalary: string; effectiveDate: string }> = {}

      // Initialize data for all plans using each plan's individual base salary
      plans.forEach((plan) => {
        const planKey = getPlanKey(plan.id, plan.revision)
        newEditableData[planKey] = {
          baseSalary: plan.baseSalary.toString(),
          effectiveDate: effectiveDate,
        }
      })

      setEditablePlansData(newEditableData)
    }
  }, [compPlan, selectedPeriodId, periods, plans])

  // Update selected period label when period ID changes
  useEffect(() => {
    if (selectedPeriodId && periods.length > 0) {
      const selectedPeriod = findPeriodById(periods, selectedPeriodId)
      if (selectedPeriod?.label) {
        setSelectedPeriodLabel(selectedPeriod.label)
      } else {
        setSelectedPeriodLabel('Selected Period')
      }
    } else {
      setSelectedPeriodLabel('Selected Period')
    }

    // Clear any validation error since we now exclude the current period from the selector
    setValidationError('')
  }, [selectedPeriodId, periods])

  // Effect to handle rollover promise completion
  useEffect(() => {
    if (rolloverPromise) {
      rolloverPromise
        .then(() => {
          setActiveStep('plan-review')
        })
        .catch((error) => {
          console.error('Error in rollover process:', error)
          enqueueSnackbar('Failed to complete rollover process', { variant: 'error' })
        })
    }
  }, [rolloverPromise, enqueueSnackbar])

  const handlePrepareRollover = () => {
    setIsPreparingRollover(true)

    const selectedPeriod = findPeriodById(periods, selectedPeriodId)
    if (!selectedPeriod?.rootPeriodId) {
      enqueueSnackbar('Invalid period selection', { variant: 'error' })
      setIsPreparingRollover(false)
      return
    }

    const templateRevision = compPlan?.plan.templateRevision ?? 1

    // Include all current plans for user target extraction
    const planIdsParam = plans.map((plan) => `${plan.id}:${plan.revision}`).join(',')

    // Use the first plan's template ID since all plans share the same template
    const templateId = firstPlan?.templateId || ''

    prepareTemplateRollover(templateId, selectedPeriod.rootPeriodId, selectedPeriodId, templateRevision, planIdsParam)
      .then((response) => {
        setPrepareResponse(response)

        // Always show target review step first, regardless of existing template
        if (response.existingTemplate) {
          setTemplate(response.existingTemplate.template)
        }

        // Store template and individual targets separately
        const templateTargetPairs = (response.targetPairs || []).filter((pair) => pair.nextTarget != null)

        // Only include individual targets for components that are NOT organization-level
        const individualTargetPairs = (response.planTargetPairs || []).filter((pair) => {
          if (!pair.nextTarget) return false

          // Find the component for this target
          const component = response.components?.find((c) => c.id === pair.nextTarget!.componentId)

          // Only include if component is individual-level (not organization-level)
          return component?.isOrganizationLevel === false
        })

        setTemplateTargetPairs(templateTargetPairs)
        setIndividualTargetPairs(individualTargetPairs)

        // Initialize composite values from existing targets
        const initialCompositeValues: Record<string, CompositeTargetValueItem[]> = {}

        // Process both template and individual target pairs
        const allPairs = [...templateTargetPairs, ...individualTargetPairs]
        allPairs.forEach((pair) => {
          if (!pair.nextTarget) return

          const component = response.components?.find((c) => c.id === pair.originalTarget.componentId)
          if (component?.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
            const compositeKey = `${pair.originalTarget.componentId}-${pair.nextTarget.periodId}-${pair.originalTarget.userId || 'template'}`

            // Try to parse existing target value as CompositeTargetValue
            try {
              const parsedTarget = JSON.parse(pair.originalTarget.value.toString()) as CompositeTargetValue
              if (parsedTarget.compositeTargetValues && Array.isArray(parsedTarget.compositeTargetValues)) {
                initialCompositeValues[compositeKey] = parsedTarget.compositeTargetValues
              }
            } catch (e) {
              // If parsing fails, initialize with empty array
              initialCompositeValues[compositeKey] = []
            }
          }
        })

        setCompositeTargetValues(initialCompositeValues)
        setActiveStep('target-review')
      })
      .catch((error) => {
        console.error('Error preparing template rollover:', error)
        enqueueSnackbar('Failed to prepare template rollover', { variant: 'error' })
      })
      .finally(() => {
        setIsPreparingRollover(false)
      })
  }

  const handleExecuteRollover = () => {
    if (!prepareResponse || !selectedPeriodId || !fromPeriodId) return

    setIsExecutingRollover(true)

    const selectedPeriod = findPeriodById(periods, selectedPeriodId)
    if (!selectedPeriod?.rootPeriodId) {
      enqueueSnackbar('Invalid period selection', { variant: 'error' })
      setIsExecutingRollover(false)
      return
    }

    const fromPeriod = findPeriodById(periods, fromPeriodId)
    if (!fromPeriod?.rootPeriodId) {
      enqueueSnackbar('Invalid source period', { variant: 'error' })
      setIsExecutingRollover(false)
      return
    }

    const templateRevision = compPlan?.plan.templateRevision ?? 1

    // Only send template targets for template rollover (individual targets handled during plan creation)
    const allTargetPairs = templateTargetPairs

    const request = {
      targetPairs: allTargetPairs,
      rootOrganizationId: prepareResponse.organizationId,
      templateId: prepareResponse.templateId,
      templateRevision: templateRevision,
      nextRootPeriodId: selectedPeriod.rootPeriodId,
      nextPeriodId: selectedPeriodId,
      prevRootPeriodId: fromPeriod.rootPeriodId,
      prevPeriodId: fromPeriodId,
    }

    executeTemplateRollover(prepareResponse.templateId, request)
      .then((rolloverResponse) => {
        setTemplate(rolloverResponse.template)
        setActiveStep('plan-edit')
      })
      .catch((error) => {
        console.error('Error executing template rollover:', error)
        enqueueSnackbar('Failed to execute template rollover', { variant: 'error' })
      })
      .finally(() => {
        setIsExecutingRollover(false)
      })
  }

  const handleContinue = () => {
    handlePrepareRollover()
  }

  const handleBack = () => {
    if (activeStep === 'plan-review') {
      setActiveStep('plan-edit')
    } else if (activeStep === 'plan-edit') {
      // Go back to target-review if we came from there, otherwise select-period
      if (templateTargetPairs.length > 0 || individualTargetPairs.length > 0) {
        setActiveStep('target-review')
      } else {
        setActiveStep('select-period')
        setPrepareResponse(null)
        setTemplate(null)
        if (showSelectPeriodStep) {
          setSelectedPeriodId('')
          setValidationError('')
        }
      }
    } else if (activeStep === 'target-review') {
      setActiveStep('select-period')
      setPrepareResponse(null)
      setTemplate(null)
      setTemplateTargetPairs([])
      setIndividualTargetPairs([])
      if (showSelectPeriodStep) {
        setSelectedPeriodId('')
        setValidationError('')
      }
    }
  }

  const handleMoveToPlanEdit = async () => {
    await handleExecuteRollover()
  }

  const handleTemplateTargetChange = (targetIndex: number, newValue: any) => {
    const newTargetPairs = [...templateTargetPairs]
    if (newTargetPairs[targetIndex].nextTarget) {
      const pair = newTargetPairs[targetIndex]
      const compositeKey = `${pair.originalTarget.componentId}-${pair.nextTarget!.periodId}-${pair.originalTarget.userId || 'template'}`

      // Check if this is a composite component
      const component =
        prepareResponse?.components?.find((c) => c.id === pair.originalTarget.componentId) ||
        compPlan?.components?.find((c) => c.id === pair.originalTarget.componentId)

      let finalValue = newValue

      // If composite, build the CompositeTargetValue structure
      if (component?.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        const compositeValues = compositeTargetValues[compositeKey] || []
        const compositeTargetValue: CompositeTargetValue = {
          value: newValue.toString(),
          compositeTargetValues: compositeValues,
        }
        finalValue = JSON.stringify(compositeTargetValue)
      }

      newTargetPairs[targetIndex] = {
        ...newTargetPairs[targetIndex],
        nextTarget: {
          ...newTargetPairs[targetIndex].nextTarget!,
          value: finalValue,
        },
      }
      setTemplateTargetPairs(newTargetPairs)
    }
  }

  const handleIndividualTargetChange = (targetIndex: number, newValue: any) => {
    const newTargetPairs = [...individualTargetPairs]
    if (newTargetPairs[targetIndex].nextTarget) {
      const pair = newTargetPairs[targetIndex]
      const compositeKey = `${pair.originalTarget.componentId}-${pair.nextTarget!.periodId}-${pair.originalTarget.userId || 'template'}`

      // Check if this is a composite component
      const component =
        prepareResponse?.components?.find((c) => c.id === pair.originalTarget.componentId) ||
        compPlan?.components?.find((c) => c.id === pair.originalTarget.componentId)

      let finalValue = newValue

      // If composite, build the CompositeTargetValue structure
      if (component?.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        const compositeValues = compositeTargetValues[compositeKey] || []
        const compositeTargetValue: CompositeTargetValue = {
          value: newValue.toString(),
          compositeTargetValues: compositeValues,
        }
        finalValue = JSON.stringify(compositeTargetValue)
      }

      newTargetPairs[targetIndex] = {
        ...newTargetPairs[targetIndex],
        nextTarget: {
          ...newTargetPairs[targetIndex].nextTarget!,
          value: finalValue,
        },
      }
      setIndividualTargetPairs(newTargetPairs)
    }
  }

  const handleUpdateCompositeValue = (
    compositeKey: string,
    valueIndex: number,
    field: 'label' | 'description' | 'target',
    newValue: string
  ) => {
    const currentValues = compositeTargetValues[compositeKey] || []
    const newValues = {
      ...compositeTargetValues,
      [compositeKey]: currentValues.map((item, i) => (i === valueIndex ? { ...item, [field]: newValue } : item)),
    }
    setCompositeTargetValues(newValues)
  }

  const toggleCompositeExpansion = (compositeKey: string) => {
    setExpandedCompositeRows((prev) => ({
      ...prev,
      [compositeKey]: !prev[compositeKey],
    }))
  }

  const handleCreatePlan = async () => {
    if (!template || !compPlan?.plan) {
      enqueueSnackbar('Missing template or plan data', { variant: 'error' })
      return
    }

    // Get the selected period
    const selectedPeriod = findPeriodById(periods, selectedPeriodId)
    if (!selectedPeriod) {
      enqueueSnackbar('Invalid period selection', { variant: 'error' })
      return
    }

    try {
      const templateId = 'template' in template ? template.template.id : template.id
      const templateRevision = 'template' in template ? template.template.revision : template.revision

      const newTemplateTargets = template && 'targets' in template ? template.targets : []

      // Create a map of template targets using componentId-periodId as key
      const templateTargetMap = new Map<string, string>()
      for (const target of newTemplateTargets) {
        const key = `${target.componentId}-${target.periodId}`
        templateTargetMap.set(key, target.slug)
      }
      console.log('Template target map:', templateTargetMap)

      // Use the rolled-over template's variable allocations (with updated slugs) instead of the original plan's allocations
      const templateVariableAllocations =
        'template' in template ? template.template.variableAllocations : template.variableAllocations
      // Keep row periods that already sit inside the selected period (the template rollover maps
      // sub-period rows, e.g. monthly rows in a quarter); only rows outside it fall back to the
      // selected period. Forcing every row to the selected period detaches them from their targets.
      const updatedVariableAllocations = templateVariableAllocations.map((table: any) => ({
        ...table,
        rows: table.rows.map((row: any) => ({
          ...row,
          periodId: row.periodId && findPeriodById([selectedPeriod], row.periodId) ? row.periodId : selectedPeriodId,
        })),
      }))

      // Get all component IDs from the original plan
      const planComponentIds = compPlan.components?.map((c) => c.id).filter((id): id is string => !!id) || []

      let successCount = 0
      let failCount = 0

      // Create plans for each employee
      for (const plan of plans) {
        try {
          const planKey = getPlanKey(plan.id, plan.revision)
          const planData = editablePlansData[planKey] || { baseSalary: '0', effectiveDate: '' }

          // Only process individual target pairs for this specific user
          const processedTargets = individualTargetPairs
            .filter((pair) => pair.nextTarget != null)
            .filter((pair) => pair.originalTarget.userId === plan.userId) // Filter for this specific user
            .filter((pair) => {
              // Only include individual-level components (exclude organization-level)
              const component =
                prepareResponse?.components?.find((c) => c.id === pair.nextTarget!.componentId) ||
                compPlan?.components?.find((c) => c.id === pair.nextTarget!.componentId)
              return component?.isOrganizationLevel === false
            })
            .map((pair) => {
              // Find the component to get its label
              const component = compPlan?.components?.find((c) => c.id === pair.nextTarget!.componentId)
              const componentLabel = component?.label || 'Unknown Component'

              // Attempt to lookup the template target slug using componentId-periodId
              const templateLookupKey = `${pair.nextTarget!.componentId}-${pair.nextTarget!.periodId || selectedPeriodId}`
              const templateSlug = templateTargetMap.get(templateLookupKey)

              return {
                componentId: pair.nextTarget!.componentId,
                periodId: pair.nextTarget!.periodId || selectedPeriodId,
                value: pair.nextTarget!.value?.toString() || '',
                slug: templateSlug || pair.nextTarget!.slug || '', // Use template slug if found, otherwise use original
                userId: plan.userId,
                organizationId: pair.nextTarget!.organizationId || compPlan.plan.organizationId,
                rootOrganizationId: pair.nextTarget!.rootOrganizationId || compPlan.plan.rootOrganizationId,
                rootPeriodId: pair.nextTarget!.rootPeriodId || selectedPeriod.rootPeriodId || selectedPeriod.id || '',
                label: componentLabel, // Use proper component label for slug generation
              }
            })
            .filter((target) => target.componentId)

          const targetComponentIds = processedTargets.map((target) => target.componentId)
          const allComponentIds = [...new Set([...planComponentIds, ...targetComponentIds])]

          // Build the plan creation request for this employee
          const createPlanRequest = {
            plan: {
              userId: plan.userId,
              templateId: templateId || undefined,
              templateRevision,
              organizationId: compPlan.plan.organizationId,
              rootOrganizationId: compPlan.plan.rootOrganizationId,
              rootPeriodId: selectedPeriod.rootPeriodId || selectedPeriod.id || '',
              periodId: selectedPeriodId,
              periodProration: plan.periodProration,
              position: plan.position,
              compensationModel: plan.compensationModel,
              baseSalary: parseFloat(planData.baseSalary) || 0,
              variablePercent: plan.variablePercent,
              variableCap: plan.variableCap,
              variableAllocations: updatedVariableAllocations,
              effectiveDate: dayjs.utc(planData.effectiveDate).toISOString(),
            },
            components: allComponentIds,
            targets: processedTargets,
          }

          await createPlan(createPlanRequest)
          successCount++
        } catch (error) {
          console.error(`Error creating plan for ${plan.employeeFirstName} ${plan.employeeLastName}:`, error)
          failCount++
        }
      }

      // Show appropriate success/error message
      if (failCount === 0) {
        enqueueSnackbar(`Successfully created ${successCount} plan${successCount !== 1 ? 's' : ''}`, {
          variant: 'success',
        })
      } else if (successCount === 0) {
        enqueueSnackbar(`Failed to create all ${failCount} plan${failCount !== 1 ? 's' : ''}`, { variant: 'error' })
      } else {
        enqueueSnackbar(`Created ${successCount} plan${successCount !== 1 ? 's' : ''}, failed to create ${failCount}`, {
          variant: 'warning',
        })
      }

      // Trigger data refresh by calling onRollover with the selected period
      onRollover(selectedPeriodId)

      onClose()
    } catch (error) {
      console.error('Error in bulk plan creation:', error)
      enqueueSnackbar('Failed to create plans', { variant: 'error' })
    }
  }

  const renderPlanReview = () => {
    if (!compPlan?.plan) return null

    const templateLabel = 'template' in template! ? template.template.label : template!.label

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Please review and update the plan details for each employee.
        </Alert>

        <TableContainer>
          <Table size="small" aria-label="plan-edit-table">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Employee
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Template
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Period
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Annualized Compensation
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Effective Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const planKey = getPlanKey(plan.id, plan.revision)
                const planData = editablePlansData[planKey] || { baseSalary: '0', effectiveDate: '' }

                return (
                  <TableRow key={planKey}>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {plan.employeeFirstName} {plan.employeeLastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {templateLabel}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {selectedPeriodLabel}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Box sx={{ width: '80%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <CurrencyField
                          value={planData.baseSalary}
                          onChange={(value) =>
                            setEditablePlansData((prev) => ({
                              ...prev,
                              [planKey]: { ...prev[planKey], baseSalary: value },
                            }))
                          }
                          name={`baseSalary-${planKey}`}
                          currency="USD"
                          currencyDecimals={0}
                          variant="outlined"
                          pill
                          shrink
                          style={{ width: '100%' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Box sx={{ width: '80%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <TextField
                          type="date"
                          value={planData.effectiveDate}
                          onChange={(e) =>
                            setEditablePlansData((prev) => ({
                              ...prev,
                              [planKey]: { ...prev[planKey], effectiveDate: e.target.value },
                            }))
                          }
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '21px',
                              fontSize: '0.875rem',
                              '& fieldset': {
                                borderRadius: '21px',
                              },
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const renderPlanReviewReadOnly = () => {
    if (!compPlan?.plan) return null

    const templateLabel = 'template' in template! ? template.template.label : template!.label

    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          Review the plan details before creating the plans.
        </Alert>

        <TableContainer>
          <Table size="small" aria-label="plan-review-table">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Employee
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Template
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Period
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  {plans[0]?.compensationModel === 'BASE_PLUS' ? 'Period Compensation' : 'Period Base Compensation'}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Effective Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const planKey = getPlanKey(plan.id, plan.revision)
                const planData = editablePlansData[planKey] || { baseSalary: '0', effectiveDate: '' }

                return (
                  <TableRow key={planKey}>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {plan.employeeFirstName} {plan.employeeLastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {templateLabel}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {selectedPeriodLabel}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        ${(parseFloat(planData.baseSalary) * plan.periodProration).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatDisplayDate(planData.effectiveDate)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const renderCompositeValuesTable = (
    compositeKey: string,
    componentType: ComponentType,
    component: any,
    originalValue: any,
    precision?: number
  ) => {
    const values = compositeTargetValues[compositeKey] || []

    // Parse original composite target value to get previous targets
    let originalCompositeValues: CompositeTargetValueItem[] = []
    try {
      const parsedOriginal = JSON.parse(originalValue.toString()) as CompositeTargetValue
      if (parsedOriginal.compositeTargetValues && Array.isArray(parsedOriginal.compositeTargetValues)) {
        originalCompositeValues = parsedOriginal.compositeTargetValues
      }
    } catch (e) {
      // If parsing fails, empty array
    }

    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Composite Values
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="25%">Label</TableCell>
                <TableCell width="35%">Description</TableCell>
                <TableCell width="20%" align="center">
                  Previous Target
                </TableCell>
                <TableCell width="20%" align="center">
                  New Target
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {values.map((item, valueIndex) => {
                const originalItem = originalCompositeValues.find((orig) => orig.label === item.label)
                return (
                  <TableRow key={valueIndex}>
                    <TableCell>
                      <Typography variant="body2">{item.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {item.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <ComponentDisplay
                        componentType={originalItem?.componentType || item.componentType || componentType}
                        value={originalItem?.target || '0'}
                        fontSize="14px"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <ComponentField
                        componentType={item.componentType || componentType}
                        label=""
                        value={item.target}
                        onChange={(value) => handleUpdateCompositeValue(compositeKey, valueIndex, 'target', value)}
                        name={`${compositeKey}-composite-${valueIndex}`}
                        currency="USD"
                        currencyDecimals={0}
                        decimalPlaces={precision || 2}
                        style={{ width: '100%' }}
                        variant="outlined"
                        pill
                        shrink
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
              {values.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No composite values found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const renderTargetTable = (
    targetPairs: TargetPair[],
    title: string,
    handleChange: (index: number, value: any) => void
  ) => {
    if (targetPairs.length === 0) return null

    // Sort targetPairs so that items with hasTargetSlug show up last
    // Keep track of original indices for proper updates
    const indexedPairs = targetPairs.map((pair, originalIndex) => ({ pair, originalIndex }))
    const sortedIndexedPairs = [...indexedPairs].sort((a, b) => {
      const aHasTargetSlug = a.pair.nextTarget && a.pair.nextTarget.slug != null && a.pair.nextTarget.slug !== ''
      const bHasTargetSlug = b.pair.nextTarget && b.pair.nextTarget.slug != null && b.pair.nextTarget.slug !== ''
      return Number(aHasTargetSlug) - Number(bHasTargetSlug)
    })

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
          {title}
        </Typography>

        <TableContainer>
          <Table size="small" aria-label={`${title.toLowerCase()}-table`}>
            <TableHead>
              <TableRow>
                <TableCell align="center">Component</TableCell>
                <TableCell align="center">Previous Period</TableCell>
                <TableCell align="center">New Period</TableCell>
                <TableCell align="center">Previous Target</TableCell>
                <TableCell align="center">New Target</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedIndexedPairs.map(({ pair, originalIndex }) => {
                if (!pair.nextTarget) return null

                const originalPeriodLabel = pair.originalTarget.periodId
                  ? periodIdLabelMap[pair.originalTarget.periodId]
                  : ''
                const nextPeriodLabel = pair.nextTarget.periodId ? periodIdLabelMap[pair.nextTarget.periodId] : ''

                // Find component from response or plan data for proper labeling
                const component =
                  prepareResponse?.components?.find((c) => c.id === pair.originalTarget.componentId) ||
                  compPlan?.components?.find((c) => c.id === pair.originalTarget.componentId)
                const hasTargetSlug = pair.nextTarget.slug != null && pair.nextTarget.slug !== ''
                const componentType = component ? component.componentType : ComponentType.COMPONENT_TYPE_FLOAT

                // Determine label based on component type and userId
                let displayLabel = component?.label || 'Unknown Component'

                if (component?.isOrganizationLevel === false) {
                  // Individual level component
                  if (pair.originalTarget.userId === '00000000-0000-0000-0000-000000000000') {
                    // uuid.Nil = default template target
                    displayLabel = `${component.label} (default)`
                  } else if (pair.originalTarget.userId) {
                    // Find the employee for this target
                    const targetPlan = plans.find((p) => p.userId === pair.originalTarget.userId)
                    const employeeName = targetPlan
                      ? `${targetPlan.employeeFirstName} ${targetPlan.employeeLastName}`
                      : 'Unknown Employee'
                    displayLabel = `${component.label} (${employeeName})`
                  }
                }
                // Organization level components show just the label (no suffix)

                // Create composite key: componentId-periodId-userId
                const compositeKey = `${pair.originalTarget.componentId}-${pair.nextTarget.periodId}-${pair.originalTarget.userId || 'template'}`
                const isComposite = component?.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
                const isExpanded = expandedCompositeRows[compositeKey] || false

                // For composite targets, extract the overall value from the JSON structure
                let displayPreviousValue: any = pair.originalTarget.value
                let displayNextValue: any = pair.nextTarget.value
                if (isComposite) {
                  try {
                    const parsedPrevious = JSON.parse(pair.originalTarget.value.toString()) as CompositeTargetValue
                    displayPreviousValue = parsedPrevious.value
                  } catch (e) {
                    // If parsing fails, use original value
                  }

                  try {
                    const parsedNext = JSON.parse(pair.nextTarget.value.toString()) as CompositeTargetValue
                    displayNextValue = parsedNext.value
                  } catch (e) {
                    // If parsing fails, use original value
                  }
                }

                return (
                  <React.Fragment key={compositeKey}>
                    <TableRow>
                      <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isComposite && (
                            <IconButton
                              size="small"
                              onClick={() => toggleCompositeExpansion(compositeKey)}
                              sx={{
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                                mr: 1,
                              }}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          )}
                          {displayLabel}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                        {originalPeriodLabel}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                        {nextPeriodLabel}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                        <ComponentDisplay componentType={componentType} value={displayPreviousValue} fontSize="14px" />
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                        <Box sx={{ width: '70%', margin: '0 auto', display: 'flex', justifyContent: 'flex-center' }}>
                          {hasTargetSlug || (pair.existingTarget && !pair.originalTarget.userId) ? (
                            <Tooltip
                              title={
                                hasTargetSlug
                                  ? 'This target is already approved and cannot be changed when rolling over the template.'
                                  : 'This template target already exists for the target period.'
                              }
                              slotProps={{ tooltip: { sx: { pointerEvents: 'auto' } } }}
                            >
                              <span>
                                <ComponentDisplay
                                  componentType={componentType}
                                  value={displayNextValue}
                                  fontSize="14px"
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <ComponentField
                              componentType={componentType}
                              value={displayNextValue}
                              onChange={(value) => handleChange(originalIndex, value)}
                              name={compositeKey}
                              style={{ width: '100%' }}
                              variant="outlined"
                              pill
                              shrink
                              currency="USD"
                              currencyDecimals={0}
                              decimalPlaces={component?.precision || component?.numericDecimalPlaces || 2}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {isComposite && isExpanded && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ backgroundColor: muiTheme.palette.action.hover, p: 2 }}>
                          {renderCompositeValuesTable(
                            compositeKey,
                            componentType,
                            component,
                            pair.originalTarget.value,
                            component?.precision || component?.numericDecimalPlaces
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const renderTargetReview = () => {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Please review and adjust the target values that will be rolled over to the new period.
        </Alert>

        {renderTargetTable(templateTargetPairs, 'Template Targets', handleTemplateTargetChange)}
        {renderTargetTable(individualTargetPairs, 'Individual Targets', handleIndividualTargetChange)}
      </Box>
    )
  }

  return (
    <RolloverContext.Provider value={{ promise: rolloverPromise, setPromise: setRolloverPromise }}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: 800,
          },
        }}
      >
        <DialogTitle color="primary">
          {plans.length === 1
            ? `Rollover ${plans[0].employeeFirstName} ${plans[0].employeeLastName}'s ${fromPeriodLabel} Plan`
            : `Rollover ${plans.length} ${fromPeriodLabel} Plans`}
        </DialogTitle>
        <DialogContent>
          {showSelectPeriodStep && activeStep === 'select-period' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                maxWidth: '600px',
                margin: '0 auto',
                pt: 4,
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" color="primary" gutterBottom sx={{ mb: 2 }}>
                  Select Target Period to Rollover Plan
                </Typography>
                <Typography variant="body1" gutterBottom>
                  This will create a new plan based on the latest approved revision of the current plan. The new plan
                  will be created for the selected period.
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Note: Any pending changes in unapproved revisions will not be included in the rollover.
                </Typography>
              </Box>
              <Box sx={{ width: '100%', maxWidth: '400px' }}>
                <SinglePeriodSelector
                  periods={periods}
                  depthOnly={planPeriodDepth}
                  excludePeriodId={fromPeriodId}
                  onChange={setSelectedPeriodId}
                  value={selectedPeriodId}
                  label="Select Target Period"
                  sortOrder="asc"
                  variant="outlined"
                  pill
                  shrink
                  disableClosed
                />
                {validationError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {validationError}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : activeStep === 'target-review' ? (
            renderTargetReview()
          ) : activeStep === 'plan-edit' ? (
            renderPlanReview()
          ) : activeStep === 'plan-review' ? (
            renderPlanReviewReadOnly()
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={onClose}
            disabled={isLoading || isPreparingRollover || isExecutingRollover || isPlanLoading || isCreatingPlan}
            variant="contained"
            color="warning"
            sx={{ marginRight: 'auto' }}
          >
            Cancel
          </Button>
          {activeStep === 'select-period' ? (
            <Button
              onClick={handleContinue}
              color="primary"
              variant="contained"
              disabled={
                !selectedPeriodId ||
                isLoading ||
                isPreparingRollover ||
                isExecutingRollover ||
                isPlanLoading ||
                isCreatingPlan
              }
              startIcon={isPreparingRollover ? <CircularProgress size={20} /> : undefined}
            >
              Next
            </Button>
          ) : (
            <>
              <Button
                onClick={handleBack}
                disabled={isLoading || isPreparingRollover || isExecutingRollover || isPlanLoading || isCreatingPlan}
                variant="outlined"
                color="primary"
              >
                Back
              </Button>
              <Button
                onClick={
                  activeStep === 'target-review'
                    ? handleMoveToPlanEdit
                    : activeStep === 'plan-edit'
                      ? () => setActiveStep('plan-review')
                      : handleCreatePlan
                }
                color="primary"
                variant="contained"
                disabled={isLoading || isPreparingRollover || isExecutingRollover || isPlanLoading || isCreatingPlan}
                startIcon={
                  isLoading || isExecutingRollover || isCreatingPlan ? <CircularProgress size={20} /> : undefined
                }
                sx={{ ml: 1 }}
              >
                {activeStep === 'target-review' ? 'Next' : activeStep === 'plan-edit' ? 'Review' : 'Create Plans'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </RolloverContext.Provider>
  )
}

export default RolloverPlanDialog
